import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, MessageCircle, MapPin, ArrowUpRight } from "lucide-react";
import Reveal, { SectionLabel } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { BRAND } from "../lib/site";
import { useSettings } from "../lib/SettingsContext";

export default function Contact() {
  const [d, setD] = useState({ name: "", email: "", phone: "", message: "" });
  const navigate = useNavigate();
  const s = useSettings();
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const phone = s.phone || BRAND.phone;
  const email = s.email || BRAND.email;
  const whatsapp = (s.whatsapp || BRAND.whatsapp).replace(/\D/g, "");
  const address = s.address || BRAND.address;
  const mapSrc = s.maps_link || BRAND.mapEmbed;

  const submit = (e) => {
    e.preventDefault();
    const subject = `BTSA Contact — ${d.name}`;
    const body = `Name: ${d.name}\nEmail: ${d.email}\nPhone: ${d.phone}\n\n${d.message}`;
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setTimeout(() => navigate("/thank-you"), 600);
  };

  return (
    <>
      <PageHero
        label="Contact"
        title={<>We'd love to <em className="font-serif italic gold-text">hear from you.</em></>}
        subtitle="Whether you're enrolling, partnering, visiting or simply curious — our doors and inbox are open."
      />

      <section data-testid="contact-grid" className="py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            {[
              { icon: Phone, label: "Phone", value: phone, href: `tel:${phone.replace(/\s/g, "")}` },
              { icon: Mail, label: "Email", value: email, href: `mailto:${email}` },
              { icon: MessageCircle, label: "WhatsApp", value: phone, href: `https://wa.me/${whatsapp}` },
              { icon: MapPin, label: "Address", value: address, href: "#map" },
            ].map((c, i) => (
              <Reveal key={c.label} delay={i * 0.05}>
                <a
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  data-testid={`contact-${c.label.toLowerCase()}`}
                  className="lux-card flex items-start gap-5 p-7 group"
                >
                  <div className="h-12 w-12 grid place-items-center hairline text-[#d4af37] shrink-0 group-hover:bg-[#d4af37] group-hover:text-black transition-all duration-500">
                    <c.icon size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="section-label">{c.label}</div>
                    <div className="mt-2 font-serif text-xl text-white font-light break-words">{c.value}</div>
                  </div>
                  <ArrowUpRight size={16} className="ml-auto text-white/30 group-hover:text-[#d4af37] transition-colors" />
                </a>
              </Reveal>
            ))}
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="lux-card p-8 lg:p-10"
              >
                <SectionLabel>Send A Message</SectionLabel>
                <h2 className="mt-5 font-serif text-3xl text-white font-light">
                  Start a conversation
                </h2>
                <div className="mt-8 grid sm:grid-cols-2 gap-5">
                  {[
                    ["name", "Name", "text"],
                    ["email", "Email", "email"],
                    ["phone", "Phone", "tel"],
                  ].map(([k, l, t]) => (
                    <div key={k} className={k === "phone" ? "sm:col-span-2" : ""}>
                      <label className="section-label">{l}</label>
                      <input
                        data-testid={`contact-input-${k}`}
                        type={t}
                        required
                        value={d[k]}
                        onChange={(e) => set(k, e.target.value)}
                        className="mt-3 w-full bg-[#0a0a0a] border border-white/10 px-4 py-4 font-sans text-sm text-white focus:outline-none focus:border-[#d4af37] transition-colors"
                      />
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <label className="section-label">Message</label>
                    <textarea
                      data-testid="contact-input-message"
                      rows={5}
                      required
                      value={d.message}
                      onChange={(e) => set("message", e.target.value)}
                      className="mt-3 w-full bg-[#0a0a0a] border border-white/10 px-4 py-4 font-sans text-sm text-white focus:outline-none focus:border-[#d4af37] resize-none"
                    />
                  </div>
                </div>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  className="mt-9 btn-gold font-sans text-[11px] tracking-[0.3em] uppercase px-9 py-5 inline-flex items-center gap-3"
                >
                  Send Message <ArrowUpRight size={14} />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="map" data-testid="contact-map" className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="hairline overflow-hidden">
            <iframe
              title="BTSA Location"
              src={mapSrc}
              width="100%"
              height="460"
              loading="lazy"
              className="block grayscale contrast-110 brightness-75 hover:grayscale-0 transition-all duration-700"
              style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.4)" }}
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </>
  );
}
